import androidx.compose.foundation.Canvas
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.runtime.withFrameNanos
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.lerp
import kotlin.math.abs
import kotlin.math.pow
import kotlin.random.Random

private const val FIRE_COLS = 36
private const val FIRE_ROWS = 40

/**
 * How the flame is shaped:
 * - [FILL] seeds the whole width and draws filled cells — fire covering a surface (the Ember dice).
 * - [PLUME] seeds center-weighted and draws vertical energy tongues — a tapered flame that rises and
 *   breaks unevenly at the sides (the flame hats on top of the die).
 */
enum class FireShape { FILL, PLUME }

/**
 * Procedural flame overlay (classic demoscene fire): a heat field seeded hot along the bottom that
 * cools as it propagates upward, mapped through a blue/orange palette with heat driving alpha. The
 * [shape] controls the silhouette + how cells are drawn. No art assets; animates forever; runs on a
 * Skiko Canvas on web/desktop too. (The SwiftUI iOS app has its own port in BankoDice2D.swift.)
 */
@Composable
fun FireDiceOverlay(modifier: Modifier, palette: FirePalette, shape: FireShape, animated: Boolean = true) {
    val heat = remember(shape, animated) {
        FloatArray(FIRE_COLS * FIRE_ROWS).also { h ->
            // Pre-warm to a full flame for FILL (so it doesn't "grow" each roll) and for any static
            // (non-animated) preview, so the tile shows established fire rather than an empty grid.
            if (shape == FireShape.FILL || !animated) repeat(FIRE_ROWS + 10) { stepFire(h, shape) }
        }
    }
    var tick by remember { mutableStateOf(0) }

    // Static previews (e.g. the customize sheet) skip the per-frame loop entirely — running many live
    // fire sims at once is what makes the picker laggy. Only the in-game dice animate.
    LaunchedEffect(shape, animated) {
        if (!animated) return@LaunchedEffect
        var last = 0L
        while (true) {
            withFrameNanos { now ->
                // ~30fps is smooth enough for fire and halves the work vs every frame.
                if (now - last >= 33_000_000L) {
                    last = now
                    stepFire(heat, shape)
                    tick++
                }
            }
        }
    }

    Canvas(modifier = modifier) {
        tick // read so the canvas redraws after each simulation step
        val cellW = size.width / FIRE_COLS
        val cellH = size.height / FIRE_ROWS
        for (row in 0 until FIRE_ROWS) {
            for (col in 0 until FIRE_COLS) {
                val h = heat[row * FIRE_COLS + col]
                if (h <= 0.06f) continue
                val color = fireColor(h, palette)
                if (shape == FireShape.PLUME) {
                    val y = row.toFloat() / (FIRE_ROWS - 1)
                    val x = 2f * (col.toFloat() / (FIRE_COLS - 1)) - 1f
                    val noise = flameNoise(col, row, tick)
                    val halfWidth = (0.18f + y * 0.78f + (noise - 0.5f) * 0.12f).coerceIn(0.16f, 0.98f)
                    if (abs(x) > halfWidth) continue
                    if (y < 0.34f && h < 0.2f + noise * 0.45f) continue
                    if (y < 0.58f && abs(x) > 0.68f && h < 0.26f + noise * 0.32f) continue
                    val topCutoff = 0.05f + flameNoise(col, 0, tick) * 0.18f
                    val tipFade = ((y - topCutoff) / 0.18f).coerceIn(0f, 1f)
                    if (tipFade <= 0.04f) continue
                    val jitter = (((col * 13 + row * 7 + tick * 3) % 11) - 5) / 5f
                    val center = Offset((col + 0.5f) * cellW + jitter * cellW * 0.22f, (row + 0.5f) * cellH)
                    val shoulder = if (abs(x) in 0.38f..0.78f && y > 0.42f) 1.22f else 1f
                    val width = cellW * (0.55f + h * 0.6f) * (0.82f + y * 0.34f) * shoulder * (0.64f + tipFade * 0.36f)
                    val height = cellH * (1.1f + h * 2.05f) * (0.64f + y * 0.62f) * (0.52f + tipFade * 0.48f)
                    if (center.y - height * 0.66f < size.height * (0.015f + noise * 0.075f)) continue
                    val topFade = (0.5f + y * 0.5f).coerceIn(0f, 1f)
                    val plumeAlpha = (color.alpha * (1.5f + h * 0.25f) * topFade * tipFade).coerceIn(0f, 1f)
                    // Thin vertical ovals read more like flame tongues than a fuzzy dome.
                    drawOval(
                        color = color.copy(alpha = plumeAlpha),
                        topLeft = Offset(center.x - width, center.y - height * 0.66f),
                        size = Size(width * 2f, height),
                    )
                    if (h > 0.72f && y > 0.26f) {
                        drawOval(
                            color = flameCoreColor(palette).copy(alpha = (plumeAlpha * 0.62f).coerceIn(0f, 1f)),
                            topLeft = Offset(center.x - width * 0.42f, center.y - height * 0.52f),
                            size = Size(width * 0.84f, height * 0.56f),
                        )
                    }
                } else {
                    // Filled cells → fire covering the whole surface.
                    drawRect(color, Offset(col * cellW, row * cellH), Size(cellW + 0.6f, cellH + 0.6f))
                }
            }
        }
    }
}

private fun stepFire(heat: FloatArray, shape: FireShape) {
    // Seed the bottom row. FILL is hot across the full width; PLUME is center-weighted and ragged so
    // it rises as uneven flame tongues instead of a smooth party-hat cone.
    val base = (FIRE_ROWS - 1) * FIRE_COLS
    for (col in 0 until FIRE_COLS) {
        heat[base + col] = when (shape) {
            FireShape.FILL -> if (Random.nextFloat() < 0.85f) 1f else Random.nextFloat() * 0.4f
            FireShape.PLUME -> {
                val u = 2f * (col.toFloat() / (FIRE_COLS - 1)) - 1f
                val bell = (1f - abs(u).pow(4f)).coerceAtLeast(0f)
                val edgeBreak = if (abs(u) > 0.92f && Random.nextFloat() < 0.46f) 0.32f else 1f
                val lick = if (Random.nextFloat() < 0.24f) 1f else 0.78f + Random.nextFloat() * 0.2f
                val shoulderFlare = if (abs(u) in 0.42f..0.82f && Random.nextFloat() < 0.16f) 1.28f else 1f
                (bell * edgeBreak * lick * shoulderFlare).coerceIn(0f, 1f)
            }
        }
    }
    // Propagate upward: each cell averages the cells below (plus one two rows down for a taller flame),
    // then cools a little with noise so the flame shimmers and the tongues drift.
    for (row in 0 until FIRE_ROWS - 1) {
        for (col in 0 until FIRE_COLS) {
            val belowRow = row + 1
            val below = belowRow * FIRE_COLS + col
            val belowL = belowRow * FIRE_COLS + (col - 1).coerceAtLeast(0)
            val belowR = belowRow * FIRE_COLS + (col + 1).coerceAtMost(FIRE_COLS - 1)
            val below2 = (row + 2).coerceAtMost(FIRE_ROWS - 1) * FIRE_COLS + col
            val avg = if (shape == FireShape.PLUME) {
                (heat[below] * 1.22f + heat[belowL] * 0.7f + heat[belowR] * 0.7f + heat[below2] * 0.58f) / 3.08f
            } else {
                (heat[below] + heat[belowL] + heat[belowR] + heat[below2]) / 4f
            }
            val cooling = if (shape == FireShape.PLUME) 0.018f + Random.nextFloat() * 0.04f else 0.02f + Random.nextFloat() * 0.04f
            heat[row * FIRE_COLS + col] = (avg - cooling).coerceIn(0f, 1f)
        }
    }
}

private fun flameNoise(col: Int, row: Int, tick: Int): Float =
    ((col * 37 + row * 17 + tick * 11) % 100) / 100f

private fun fireColor(h: Float, palette: FirePalette): Color {
    // Cool → hot ramp per palette; alpha rises with heat so cool cells fade to transparent.
    // Hottest tops out at a warm/light tint rather than stark white, so the flame base doesn't read
    // as a harsh bright bar where it meets the die.
    val color = when (palette) {
        FirePalette.BLUE -> when {
            h < 0.45f -> lerp(Color(0xFF06104A), Color(0xFF1E5BFF), h / 0.45f)
            h < 0.8f -> lerp(Color(0xFF1E5BFF), Color(0xFF66E0FF), (h - 0.45f) / 0.35f)
            else -> lerp(Color(0xFF66E0FF), Color(0xFFCDEEFF), (h - 0.8f) / 0.2f)
        }
        FirePalette.ORANGE -> when {
            h < 0.45f -> lerp(Color(0xFF6A0E00), Color(0xFFFF4500), h / 0.45f)
            h < 0.8f -> lerp(Color(0xFFFF4500), Color(0xFFFFB02A), (h - 0.45f) / 0.35f)
            else -> lerp(Color(0xFFFFB02A), Color(0xFFFFE7A8), (h - 0.8f) / 0.2f)
        }
    }
    return color.copy(alpha = (h * 1.3f).coerceIn(0f, 1f))
}

private fun flameCoreColor(palette: FirePalette): Color = when (palette) {
    FirePalette.BLUE -> Color(0xFFDDFBFF)
    FirePalette.ORANGE -> Color(0xFFFFE8A8)
}
